import { useState, useEffect } from 'react';
import {
  Share2, Send, Loader2, CheckCircle, Mail, MessageCircle,
  Copy, Link, X, AlertTriangle, Users, UserPlus, Server
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Checkbox } from '@easyflow/ui-frontend/components/ui/checkbox';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { useSocket } from '@/contexts/SocketContext';

interface Invitation {
  id: string;
  userId: string;
  userName: string;
  email?: string;
  phone?: string;
}

interface WhatsAppSender {
  id: string;
  sender_id: string;
  phone_number: string;
  display_name?: string;
  status: string;
}

interface WhatsAppTemplate {
  id: string;
  friendly_name: string;
  body_text: string;
  approval_status: string;
  category?: string;
}

interface EmailAccount {
  id: string;
  account_type: string;
  display_name: string;
  email_address: string;
  is_default: boolean;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body_html: string;
}

interface ShareVideoLinkModalProps {
  open: boolean;
  onClose: () => void;
  room: {
    id: string;
    title: string;
    meeting_link: string;
    guest_link: string;
  } | null;
}

export default function ShareVideoLinkModal({ open, onClose, room }: ShareVideoLinkModalProps) {
  const { toast } = useToast();
  const { on, off } = useSocket();

  const [tab, setTab] = useState<string>('interni');
  const [channel, setChannel] = useState<'whatsapp' | 'email'>('email');
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState<{ sent: number; failed: number; total: number; percent: number } | null>(null);
  const [completed, setCompleted] = useState(false);
  const [currentActionId, setCurrentActionId] = useState<string | null>(null);

  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [selectedInvitees, setSelectedInvitees] = useState<string[]>([]);

  const [whatsAppSenders, setWhatsAppSenders] = useState<WhatsAppSender[]>([]);
  const [whatsAppTemplates, setWhatsAppTemplates] = useState<WhatsAppTemplate[]>([]);
  const [fromNumber, setFromNumber] = useState('');
  const [templateId, setTemplateId] = useState('');

  const [emailAccounts, setEmailAccounts] = useState<EmailAccount[]>([]);
  const [selectedEmailAccountId, setSelectedEmailAccountId] = useState('');
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
  const [emailMode, setEmailMode] = useState<'auto' | 'template'>('auto');
  const [selectedEmailTemplateId, setSelectedEmailTemplateId] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  const [guestEmails, setGuestEmails] = useState<string[]>([]);
  const [guestEmailInput, setGuestEmailInput] = useState('');
  const [guestPhones, setGuestPhones] = useState<string[]>([]);
  const [guestPhoneInput, setGuestPhoneInput] = useState('');

  const [loadingData, setLoadingData] = useState(false);

  const loadInvitations = async (roomId: string) => {
    setInvitationsLoading(true);
    try {
      const res = await apiClient.get<{ id: string; userId: string; userName: string; email?: string; phone?: string }[]>(`/video/rooms/${roomId}/invitations`);
      const data = Array.isArray(res?.data) ? res.data : [];
      setInvitations(data);
      setSelectedInvitees(data.map((inv: Invitation) => inv.userId || inv.id));
    } catch {
      setInvitations([]);
    } finally {
      setInvitationsLoading(false);
    }
  };

  const loadMessagingData = async () => {
    setLoadingData(true);
    try {
      const [sendersResult, templatesResult, emailAccResult, emailTplResult] = await Promise.allSettled([
        apiClient.get<{ senders?: WhatsAppSender[] }>('/messaging/senders?channel=whatsapp'),
        apiClient.get<{ templates?: WhatsAppTemplate[] }>('/messaging/templates?status=approved'),
        apiClient.get<{ data?: EmailAccount[] } | EmailAccount[]>('/email-accounts/all'),
        apiClient.get<{ data?: EmailTemplate[] } | EmailTemplate[]>('/email/templates'),
      ]);
      const whatsappData = sendersResult.status === 'fulfilled' ? sendersResult.value.data : undefined;
      const templatesData = templatesResult.status === 'fulfilled' ? templatesResult.value.data : undefined;
      const emailAccData = emailAccResult.status === 'fulfilled' ? emailAccResult.value.data : undefined;
      const emailTplData = emailTplResult.status === 'fulfilled' ? emailTplResult.value.data : undefined;

      const senders = Array.isArray(whatsappData?.senders) ? whatsappData.senders.filter((s: WhatsAppSender) => s.status === 'active') : [];
      setWhatsAppSenders(senders);
      setWhatsAppTemplates(Array.isArray(templatesData?.templates) ? templatesData.templates : []);

      const accList: EmailAccount[] = Array.isArray(emailAccData)
        ? emailAccData
        : Array.isArray(emailAccData?.data) ? emailAccData.data : [];
      setEmailAccounts(accList);
      const def = accList.find((a: EmailAccount) => a.is_default);
      setSelectedEmailAccountId(def?.id || (accList.length > 0 ? accList[0]!.id : ''));

      const tplList: EmailTemplate[] = Array.isArray(emailTplData)
        ? emailTplData
        : Array.isArray(emailTplData?.data) ? emailTplData.data : [];
      setEmailTemplates(tplList);
    } catch (error) {
      console.error('Error loading messaging data:', error);
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (open && room) {
      loadInvitations(room.id);
      loadMessagingData();
      setTab('interni');
      setChannel('email');
      setSending(false);
      setProgress(null);
      setCompleted(false);
      setCurrentActionId(null);
      setSelectedInvitees([]);
      setFromNumber('');
      setTemplateId('');
      setSelectedEmailTemplateId('');
      setEmailMode('auto');
      setEmailSubject('');
      setEmailBody('');
      setGuestEmails([]);
      setGuestEmailInput('');
      setGuestPhones([]);
      setGuestPhoneInput('');
    }
  }, [open, room?.id, room]);

  useEffect(() => {
    if (!open || !currentActionId) return;

    const handleProgress = (data: { actionId: string; sent: number; failed: number; total: number; percent: number }) => {
      if (data.actionId !== currentActionId) return;
      setProgress(data);
    };

    const handleCompleted = (data: { actionId: string; sent: number; failed: number; total: number }) => {
      if (data.actionId !== currentActionId) return;
      setProgress({ sent: data.sent, failed: data.failed, total: data.total, percent: 100 });
      setCompleted(true);
      setSending(false);
      toast({
        title: 'Invio completato',
        description: `${data.sent} inviati, ${data.failed} falliti su ${data.total} totali`,
      });
    };

    on('videocall-share:progress', handleProgress);
    on('videocall-share:completed', handleCompleted);
    return () => {
      off('videocall-share:progress', handleProgress);
      off('videocall-share:completed', handleCompleted);
    };
  }, [open, on, off, currentActionId, toast]);

  useEffect(() => {
    if (tab === 'interni') {
      const eligible = channel === 'email'
        ? invitations.filter(inv => inv.email)
        : invitations.filter(inv => inv.phone);
      const eligibleIds = eligible.map(inv => inv.userId || inv.id);
      setSelectedInvitees(prev => {
        const filtered = prev.filter(id => eligibleIds.includes(id));
        return filtered.length > 0 ? filtered : eligibleIds;
      });
    }
  }, [channel, invitations, tab]);

  const toggleInvitee = (id: string) => {
    setSelectedInvitees(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleAllInvitees = () => {
    const filteredInvitations = channel === 'email'
      ? invitations.filter(inv => inv.email)
      : invitations.filter(inv => inv.phone);
    const allIds = filteredInvitations.map(inv => inv.userId || inv.id);
    if (selectedInvitees.length === allIds.length) {
      setSelectedInvitees([]);
    } else {
      setSelectedInvitees(allIds);
    }
  };

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const addGuestEmail = () => {
    const email = guestEmailInput.trim();
    if (!email) return;
    if (!isValidEmail(email)) {
      toast({ title: 'Email non valida', variant: 'destructive' });
      return;
    }
    if (guestEmails.includes(email)) {
      toast({ title: 'Email già aggiunta', variant: 'destructive' });
      return;
    }
    setGuestEmails(prev => [...prev, email]);
    setGuestEmailInput('');
  };

  const addGuestPhone = () => {
    const phone = guestPhoneInput.trim();
    if (!phone) return;
    if (!/^\+?[0-9\s\-]{7,20}$/.test(phone)) {
      toast({ title: 'Numero non valido', description: 'Inserisci un numero in formato internazionale', variant: 'destructive' });
      return;
    }
    if (guestPhones.includes(phone)) {
      toast({ title: 'Numero già aggiunto', variant: 'destructive' });
      return;
    }
    setGuestPhones(prev => [...prev, phone]);
    setGuestPhoneInput('');
  };

  const handleGuestEmailKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); addGuestEmail(); }
  };

  const handleGuestPhoneKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); addGuestPhone(); }
  };

  const handleSend = async () => {
    if (!room) return;

    setSending(true);
    setProgress(null);
    setCompleted(false);

    try {
      const payload: {
        channel: 'whatsapp' | 'email';
        recipientType: string;
        fromNumber: string;
        templateId?: string;
        emailTemplateId?: string;
        emailSubject?: string;
        emailBody?: string;
        emailHtml?: string;
        emailAccountId?: string;
        selectedUserIds?: string[];
        guestEmails?: string[];
        guestPhones?: string[];
      } = {
        channel,
        recipientType: tab,
        fromNumber,
      };

      if (channel === 'whatsapp') {
        payload.templateId = templateId;
      } else {
        if (emailMode === 'template' && selectedEmailTemplateId) {
          payload.emailTemplateId = selectedEmailTemplateId;
        } else {
          payload.emailSubject = emailSubject || `Link Video Call: ${room.title}`;
          payload.emailBody = emailBody;
          payload.emailHtml = emailBody;
        }
        if (selectedEmailAccountId) {
          payload.emailAccountId = selectedEmailAccountId;
        }
      }

      if (tab === 'interni') {
        payload.selectedUserIds = selectedInvitees;
      } else {
        payload.guestEmails = guestEmails;
        payload.guestPhones = guestPhones;
      }

      const res = await apiClient.post<{ actionId: string; totalRecipients: number }>(
        `/video/rooms/${room.id}/share-links`,
        payload
      );

      if (!res.success) {
        throw new Error(res.error || 'Errore avvio invio');
      }

      const { actionId, totalRecipients } = res.data || { actionId: '', totalRecipients: 0 };
      setCurrentActionId(actionId);
      setProgress({ sent: 0, failed: 0, total: totalRecipients, percent: 0 });

      toast({
        title: 'Invio avviato',
        description: `Invio a ${totalRecipients} destinatari in corso...`,
      });
    } catch (err) {
      setSending(false);
      const msg = err instanceof Error ? err.message : String(err);
      const httpErr = err as { response?: { data?: { error?: string } } };
      toast({
        title: 'Errore',
        description: httpErr.response?.data?.error || msg || 'Errore imprevisto',
        variant: 'destructive',
      });
    }
  };

  const selectedTemplate = whatsAppTemplates.find(t => t.id === templateId);

  const filteredInvitations = channel === 'email'
    ? invitations.filter(inv => inv.email)
    : invitations.filter(inv => inv.phone);

  const canSendInterni = tab === 'interni' && selectedInvitees.length > 0 && (
    (channel === 'whatsapp' && fromNumber && templateId) ||
    (channel === 'email')
  );

  const canSendOspiti = tab === 'ospiti' && (
    (channel === 'email' && guestEmails.length > 0) ||
    (channel === 'whatsapp' && fromNumber && templateId && guestPhones.length > 0)
  );

  const canSend = canSendInterni || canSendOspiti;

  const currentLink = tab === 'ospiti' ? room?.guest_link : room?.meeting_link;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !sending) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-indigo-600" />
            Condividi Link — {room?.title}
          </DialogTitle>
        </DialogHeader>

        {completed && progress ? (
          <div className="space-y-4">
            <div className="text-center py-6">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900">Invio Completato</h3>
              <div className="flex items-center justify-center gap-6 mt-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{progress.sent}</p>
                  <p className="text-xs text-gray-500">Inviati</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{progress.failed}</p>
                  <p className="text-xs text-gray-500">Falliti</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-600">{progress.total}</p>
                  <p className="text-xs text-gray-500">Totali</p>
                </div>
              </div>
            </div>
            <Button className="w-full" onClick={onClose}>Chiudi</Button>
          </div>
        ) : sending && progress ? (
          <div className="space-y-4">
            <div className="text-center py-4">
              <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mx-auto mb-3" />
              <p className="font-medium text-gray-900">Invio in corso...</p>
              <p className="text-sm text-gray-500">{progress.sent + progress.failed} / {progress.total}</p>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-indigo-500 h-3 rounded-full transition-all duration-300"
                style={{ width: `${progress.percent}%` }}
              />
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-green-600">{progress.sent} inviati</span>
              {progress.failed > 0 && <span className="text-red-600">{progress.failed} falliti</span>}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="interni" className="gap-1.5">
                  <Users className="w-3.5 h-3.5" />
                  Interni
                </TabsTrigger>
                <TabsTrigger value="ospiti" className="gap-1.5">
                  <UserPlus className="w-3.5 h-3.5" />
                  Ospiti
                </TabsTrigger>
              </TabsList>

              <TabsContent value="interni" className="space-y-3 mt-3">
                {currentLink && (
                  <div className="flex items-center gap-2 p-2 rounded-md bg-indigo-50 border border-indigo-200">
                    <Link className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                    <span className="text-xs font-mono truncate flex-1">{currentLink}</span>
                    <button onClick={() => { navigator.clipboard.writeText(currentLink); toast({ title: 'Link copiato!' }); }} className="text-indigo-600 hover:text-indigo-800">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {invitationsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : filteredInvitations.length > 0 ? (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between px-1">
                      <Label className="text-xs text-gray-500">Destinatari ({selectedInvitees.length}/{filteredInvitations.length})</Label>
                      <button
                        className="text-xs text-indigo-600 hover:text-indigo-800"
                        onClick={toggleAllInvitees}
                      >
                        {selectedInvitees.length === filteredInvitations.length ? 'Deseleziona tutti' : 'Seleziona tutti'}
                      </button>
                    </div>
                    <ScrollArea className="max-h-40">
                      <div className="space-y-1">
                        {filteredInvitations.map((inv) => (
                          <div key={inv.id || inv.userId} className="flex items-center gap-2 p-2 rounded-md border bg-white hover:bg-gray-50">
                            <Checkbox
                              checked={selectedInvitees.includes(inv.userId || inv.id)}
                              onCheckedChange={() => toggleInvitee(inv.userId || inv.id)}
                            />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium truncate">{inv.userName || 'Utente'}</p>
                              <p className="text-xs text-gray-500 truncate">
                                {channel === 'email' ? inv.email : inv.phone}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {invitations.length > 0
                      ? `Nessun partecipante con ${channel === 'email' ? 'indirizzo email' : 'numero WhatsApp'}`
                      : 'Nessun partecipante invitato'}
                  </p>
                )}
              </TabsContent>

              <TabsContent value="ospiti" className="space-y-3 mt-3">
                {room?.guest_link && (
                  <div className="flex items-center gap-2 p-2 rounded-md bg-purple-50 border border-purple-200">
                    <Link className="w-4 h-4 text-purple-600 flex-shrink-0" />
                    <span className="text-xs font-mono truncate flex-1">{room.guest_link}</span>
                    <button onClick={() => { navigator.clipboard.writeText(room.guest_link); toast({ title: 'Link ospite copiato!' }); }} className="text-purple-600 hover:text-purple-800">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {!room?.guest_link && (
                  <div className="flex items-start gap-2 p-2 rounded-md bg-amber-50 border border-amber-200">
                    <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-700">Nessun link ospite disponibile per questa video call.</p>
                  </div>
                )}

                {channel === 'email' ? (
                  <div className="space-y-2">
                    <Label className="text-xs font-medium">Indirizzi email ospiti</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="email@esempio.com"
                        value={guestEmailInput}
                        onChange={(e) => setGuestEmailInput(e.target.value)}
                        onKeyDown={handleGuestEmailKeyDown}
                        className="flex-1 text-sm"
                      />
                      <Button variant="outline" size="sm" className="h-9" onClick={addGuestEmail} disabled={!guestEmailInput.trim()}>
                        Aggiungi
                      </Button>
                    </div>
                    {guestEmails.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {guestEmails.map((email) => (
                          <Badge key={email} variant="secondary" className="gap-1 pr-1">
                            <Mail className="w-3 h-3" />
                            {email}
                            <button onClick={() => setGuestEmails(prev => prev.filter(e => e !== email))} className="ml-0.5 hover:text-red-600">
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label className="text-xs font-medium">Numeri WhatsApp ospiti</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="+39 333 1234567"
                        value={guestPhoneInput}
                        onChange={(e) => setGuestPhoneInput(e.target.value)}
                        onKeyDown={handleGuestPhoneKeyDown}
                        className="flex-1 text-sm"
                      />
                      <Button variant="outline" size="sm" className="h-9" onClick={addGuestPhone} disabled={!guestPhoneInput.trim()}>
                        Aggiungi
                      </Button>
                    </div>
                    {guestPhones.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {guestPhones.map((phone) => (
                          <Badge key={phone} variant="secondary" className="gap-1 pr-1">
                            <MessageCircle className="w-3 h-3" />
                            {phone}
                            <button onClick={() => setGuestPhones(prev => prev.filter(p => p !== phone))} className="ml-0.5 hover:text-red-600">
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <div>
              <Label className="text-xs font-medium mb-1.5 block">Canale di invio</Label>
              <div className="flex gap-2">
                <Button
                  variant={channel === 'email' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setChannel('email')}
                  className="flex-1"
                >
                  <Mail className="w-4 h-4 mr-1" />
                  Email
                </Button>
                <Button
                  variant={channel === 'whatsapp' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setChannel('whatsapp')}
                  className="flex-1"
                >
                  <MessageCircle className="w-4 h-4 mr-1" />
                  WhatsApp
                </Button>
              </div>
            </div>

            {channel === 'whatsapp' && (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs font-medium">Mittente WhatsApp *</Label>
                  {loadingData ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                      <Loader2 className="w-4 h-4 animate-spin" /> Caricamento...
                    </div>
                  ) : (
                    <Select value={fromNumber} onValueChange={setFromNumber}>
                      <SelectTrigger className={`mt-1 ${!fromNumber ? 'border-red-300' : ''}`}>
                        <SelectValue placeholder="Seleziona numero" />
                      </SelectTrigger>
                      <SelectContent>
                        {whatsAppSenders.map((sender) => (
                          <SelectItem key={sender.id} value={sender.phone_number}>
                            {sender.display_name || sender.phone_number}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                <div>
                  <Label className="text-xs font-medium">Template WhatsApp *</Label>
                  <Select value={templateId} onValueChange={setTemplateId}>
                    <SelectTrigger className={`mt-1 ${!templateId ? 'border-red-300' : ''}`}>
                      <SelectValue placeholder="Seleziona template approvato" />
                    </SelectTrigger>
                    <SelectContent>
                      {whatsAppTemplates.map((tmpl) => (
                        <SelectItem key={tmpl.id} value={tmpl.id}>
                          <div className="flex items-center gap-2">
                            <span>{tmpl.friendly_name}</span>
                            {tmpl.category && <Badge variant="outline" className="text-[10px]">{tmpl.category}</Badge>}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedTemplate && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-gray-700">
                      <p className="font-medium text-green-700 mb-1">Anteprima:</p>
                      <p className="whitespace-pre-wrap">{selectedTemplate.body_text}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {channel === 'email' && (
              <div className="space-y-3">
                {emailAccounts.length > 0 && (
                  <div>
                    <Label className="text-xs font-medium">Account Mittente</Label>
                    <Select value={selectedEmailAccountId} onValueChange={setSelectedEmailAccountId}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Seleziona account email..." />
                      </SelectTrigger>
                      <SelectContent>
                        {emailAccounts.map(acct => (
                          <SelectItem key={acct.id} value={acct.id}>
                            <span className="flex items-center gap-2">
                              {acct.account_type === 'gmail' ? (
                                <Mail className="w-3 h-3 text-red-500 inline-block" />
                              ) : (
                                <Server className="w-3 h-3 text-blue-500 inline-block" />
                              )}
                              {acct.display_name} ({acct.email_address})
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button variant={emailMode === 'auto' ? 'default' : 'outline'} size="sm" onClick={() => setEmailMode('auto')}>
                    Automatico
                  </Button>
                  <Button variant={emailMode === 'template' ? 'default' : 'outline'} size="sm" onClick={() => setEmailMode('template')}>
                    Da Template
                  </Button>
                </div>

                {emailMode === 'auto' && (
                  <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <p className="text-xs text-indigo-700">
                      Il link della video call verrà inviato automaticamente con un messaggio predefinito contenente il titolo e il link di accesso.
                    </p>
                  </div>
                )}

                {emailMode === 'template' && (
                  <>
                    <div>
                      <Label className="text-xs font-medium">Template Email</Label>
                      <Select value={selectedEmailTemplateId} onValueChange={(v) => {
                        setSelectedEmailTemplateId(v);
                        const tpl = emailTemplates.find(t => t.id === v);
                        if (tpl) {
                          setEmailSubject(tpl.subject);
                          setEmailBody(tpl.body_html);
                        }
                      }}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Seleziona template..." />
                        </SelectTrigger>
                        <SelectContent>
                          {emailTemplates.map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Oggetto</Label>
                      <Input
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        placeholder="Oggetto email..."
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Corpo</Label>
                      <Textarea
                        value={emailBody}
                        onChange={(e) => setEmailBody(e.target.value)}
                        placeholder={`Usa {{video_call_link}} per inserire il link automaticamente`}
                        rows={4}
                        className="mt-1"
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-700">
                <p className="font-medium">Nota</p>
                <p>I messaggi verranno inviati gradualmente. Il link della video call viene incluso automaticamente nel messaggio.</p>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={onClose}>Annulla</Button>
              <Button
                onClick={handleSend}
                disabled={!canSend || sending}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Send className="w-4 h-4 mr-2" />
                Invia {tab === 'interni' ? `a ${selectedInvitees.length} interni` : `a ${channel === 'email' ? guestEmails.length : guestPhones.length} ospiti`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
